import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of, throwError } from 'rxjs';
import * as SearchBoardActions from '@store/search-board/search-board.actions';
import { ChangeSidePanelView, SearchFailure, SelectAllProperty } from '@store/search-board/search-board.actions';
import { SpinnerOverlayService } from '@shared/services/spinner-overlay.service';
import { SearchService } from '@root/search/services/search.service';
import { catchError, filter, map, mergeMap, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import {
  FloodZoneData,
  GridViewState,
  LookUpData,
  MapPropertyInfo,
  MOBILE_MAX_RECORDS_TO_SEARCH,
  MySearchRequest,
  PropertyIdentifier,
  RENDER_ID,
  ResultsCountResponse,
  SEARCH_RESULTS_COUNT_MIN_LENGTH,
  SearchField,
  SearchOperator,
  SearchRequestTypes,
  SearchResultData,
  SidePanelTab,
  SidePanelView,
  TrendsLayerData
} from '@store/search-board/search-board.model';
import { LotDimension, RangeInfo } from '@root/search/models/map.models';
import { AppState } from '@root/app-state.module';
import { select, Store } from '@ngrx/store';
import {
  selectIsMobile,
  selectMaxRecordsToSearch,
  selectPreferenceElement,
  selectPreferenceGroupById,
  selectPreferenceValue,
  selectSelectedGeos,
  selectTemplate
} from '@store/user/user.selector';
import {
  DataElement,
  DisplayOptions,
  PreferenceCode,
  PreferenceElement,
  PreferenceGroupId,
  Region,
  Template,
  TemplateCode
} from '@store/user/user.model';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ModalType } from '@shared/models/modal.model';
import { PropertySuggestionComponent } from '@root/search/components/property-suggestion/property-suggestion.component';
import {
  SearchMaximumResultsAction,
  SearchMaximumResultsComponent
} from '@root/search/components/search-maximum-results/search-maximum-results.component';
import { ActionName } from '@shared/models/effects-names.model';
import { RIBBON_TYPE } from '@shared/models/ribbon.model';
import { RibbonService } from '@shared/services/ribbon.service';
import {
  selectRecentSearches,
  selectSelectedGridView,
  selectSelectedSearch,
  selectSidePanelState,
  selectSidePanelTab
} from '@store/search-board/search-board.selector';
import { PreferenceUtils } from '@shared/utils/preference-utils';
import { NavigationService } from '@root/search/services/navigation.service';
import { MixpanelService } from '@shared/services/mixpanel.service';
import { SearchMixpanelUtil } from '@root/search/search-mixpanel-util';
import { selectedMapLayer } from '@store/map-layers.selector';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

type SearchResultsActions = SearchBoardActions.SearchLimitExceeded
  | SearchBoardActions.PropertySuggestion
  | SearchBoardActions.SearchSuccess
  | SearchBoardActions.ChangeSidePanelView
  | SearchBoardActions.RunExport
  | SearchBoardActions.RunLabelsExport;

type SearchLimitExceededActions = SearchBoardActions.StartLabelsExport
  | SearchBoardActions.StartCustomExport
  | SearchBoardActions.SearchSuccess;

@UntilDestroy()
@Injectable()
export class SearchEffects {
  private readonly searchMixpanelUtil: SearchMixpanelUtil;

  constructor(
    private readonly actions$: Actions,
    private readonly store$: Store<AppState>,
    private readonly searchService: SearchService,
    private readonly spinnerOverlayService: SpinnerOverlayService,
    private readonly dialog: MatDialog,
    private readonly ribbonService: RibbonService,
    private readonly navigationService: NavigationService,
    private readonly mixpanelService: MixpanelService
  ) {
    this.searchMixpanelUtil = new SearchMixpanelUtil(this.store$);
  }

  executeMySearch$: Observable<SearchBoardActions.HandleSearchResults | SearchBoardActions.SearchFailure> = createEffect(() => this.actions$
    .pipe(
      ofType(SearchBoardActions.MY_SEARCH),
      withLatestFrom(
        this.store$.pipe(select(selectTemplate(TemplateCode.TMPL_MY_SEARCH_DEFAULT))),
        this.store$.pipe(select(selectPreferenceGroupById(), PreferenceGroupId.COMMON_SEARCH_CONFIG)),
        this.store$.pipe(select(selectSelectedGeos)),
        this.store$.pipe(select(selectMaxRecordsToSearch))
      ),
      this.spinnerOverlayService.wrapRequest(([action, searchTemplate, preferenceGroup, selectedGeos, maxRecordsToSearch]) => {
        const mySearchPayload: MySearchRequest = {
          preferenceGroup,
          searchTemplate,
          selectedGeos,
          searchFields: (action as SearchBoardActions.MySearch).payload.searchFields,
          range: (action as SearchBoardActions.MySearch).payload.range || {},
          exportResults:  (action as SearchBoardActions.MySearch).payload.exportResults || false,
          searchRequestType: (action as SearchBoardActions.MySearch).payload.searchRequestType || SearchRequestTypes.DEFAULT,
          searchRequestData: (action as SearchBoardActions.MySearch).payload.searchRequestData
        }
        return this.searchService.executeMySearch(mySearchPayload).pipe(
          withLatestFrom(
            this.store$.pipe(select(selectSelectedGridView)),
          ),
          map(([results, gridViewState]) => {
            if (!results || !results.searchResultData || !results.searchResultData.propertySummaryList) {
              throw throwError(results);
            }

            this.mixpanelService.track('Search - Search Results Displayed', {
              searchType: 'mySearch',
              isSavedSearch: searchTemplate?.label !== 'My Search Default',
              searchParameters: mySearchPayload.searchFields.map((x) => x.fieldCode),
              countyList: selectedGeos.map((x) => x.fips),
              resultDisplay: this.gridViewState(gridViewState),
              numberOfResults: results.searchResultData.totalRecords
            });

            if (results.searchResultData.totalRecords > maxRecordsToSearch) {
              this.mixpanelService.track('Search - Search Maximum Results Displayed', {
                searchType: 'mySearch',
                isSavedSearch: searchTemplate?.label !== 'My Search Default',
                searchParameters: mySearchPayload.searchFields.map((x) => x.fieldCode),
                countyList: selectedGeos.map((x) => x.fips),
                maxLimit: maxRecordsToSearch,
              });
            }

            if (results.searchResultData.propertySummaryList.length === 0) {
              this.ribbonService.openRibbon(RIBBON_TYPE.WARNING, 'There are no results based on the search criteria(s) you entered.');
            }

            return new SearchBoardActions.HandleSearchResults({
                searchResultData: results.searchResultData,
                searchParameters: mySearchPayload.searchFields.map((x) => x.fieldCode),
                searchPayload: { ...mySearchPayload, dataElements: (action as SearchBoardActions.MySearch).payload.dataElements }
              }
            );
          }),
          catchError(() => {
            this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Something went wrong. Please try again.');
            return of(new SearchFailure());
          })
        );
      }, ActionName.MY_SEARCH_REQUEST, this.actions$.pipe(ofType(SearchBoardActions.CANCEL_MY_SEARCH_REQUEST)))
    ));

  executeQuickSearch$: Observable<SearchBoardActions.HandleSearchResults> = createEffect(() => this.actions$
    .pipe(
      ofType(SearchBoardActions.QUICK_SEARCH),
      withLatestFrom(
        this.store$.pipe(select(selectSelectedGeos)),
        this.store$.pipe(select(selectMaxRecordsToSearch))
      ),
      this.spinnerOverlayService.wrapRequest(([action, selectedGeos, maxRecordsToSearch]) => {
        return this.searchService.executeQuickSearch((action as SearchBoardActions.QuickSearch).payload).pipe(
          withLatestFrom(
            this.store$.pipe(select(selectSelectedGridView)),
          ),
          switchMap(([results, gridViewState]) => {
            if (!results || !results.propertySummaryList) {
              throw throwError(results);
            }

            this.mixpanelService.track('Search - Search Results Displayed', {
              searchType: 'quickSearch',
              isSavedSearch: false,
              searchParameters: (action as SearchBoardActions.QuickSearch).payload.searchFields.map((x) => x.fieldCode),
              countyList: selectedGeos.map((x) => x.fips),
              resultDisplay: this.gridViewState(gridViewState),
              numberOfResults: results.propertySummaryList.length
            });

            if (results.propertySummaryList.length > maxRecordsToSearch) {
              this.mixpanelService.track('Search - Search Maximum Results Displayed', {
                searchType: 'quickSearch',
                isSavedSearch: false,
                searchParameters: (action as SearchBoardActions.QuickSearch).payload.searchFields.map((x) => x.fieldCode),
                countyList: selectedGeos.map((x) => x.fips),
                maxLimit: maxRecordsToSearch,
              });
            }

            if (results.propertySummaryList.length === 0) {
              this.ribbonService.openRibbon(RIBBON_TYPE.WARNING, 'There are no results based on the search criteria(s) you entered.');
            }
            return [
              new SearchBoardActions.HandleSearchResults({
                searchResultData: results,
                searchParameters: (action as SearchBoardActions.QuickSearch).payload.searchFields.map((x) => x.fieldCode),
                searchPayload: (action as SearchBoardActions.QuickSearch).payload
              }),
              new SearchBoardActions.SaveRecentSearches()
            ];
          }),
          catchError(() => {
            this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Something went wrong. Please try again.');
            return of(void (0));
          })
        );
      }, ActionName.QUICK_SEARCH_REQUEST, this.actions$.pipe(ofType(SearchBoardActions.CANCEL_MY_SEARCH_REQUEST))),
      filter<SearchBoardActions.HandleSearchResults>(Boolean)
    ));

  saveRecentSearches$ = createEffect(() => this.actions$
    .pipe(
      ofType(SearchBoardActions.SAVE_RECENT_SEARCHES),
      withLatestFrom(this.store$.pipe(select(selectRecentSearches))),
      tap(([_, recentSearches]) => window.localStorage.setItem('recentSearches', JSON.stringify(recentSearches)))
    ), {dispatch: false});

  executeLastSearch$: Observable<SearchBoardActions.QuickSearch | SearchBoardActions.MySearch
    | SearchBoardActions.ClearSearchResults> = createEffect(() => this.actions$
    .pipe(
      ofType(SearchBoardActions.QUICK_SEARCH, SearchBoardActions.MY_SEARCH, SearchBoardActions.CLEAR_SEARCH_RESULTS),
      switchMap((action: SearchBoardActions.QuickSearch | SearchBoardActions.MySearch | SearchBoardActions.ClearSearchResults) => {
        return this.actions$.pipe(
          ofType(SearchBoardActions.EXECUTE_LAST_SEARCH),
          take(1),
          map(() => action)
        );
      })
    ));

  getSearchResultsCount: Observable<SearchBoardActions.GetSearchResultsCountSuccess |
    SearchBoardActions.GetSearchResultsCountFailure |
    SearchBoardActions.ClearSearchResultsCount> = createEffect(() => this.actions$
    .pipe(
      ofType(SearchBoardActions.GET_SEARCH_RESULTS_COUNT),
      switchMap((action: SearchBoardActions.GetSearchResultsCount) => {
        if (!this.hasSearchCountFields(action.payload)) {
          return of(new SearchBoardActions.ClearSearchResultsCount());
        }
        return this.searchService.getSearchCount(action.payload).pipe(
          map((results: ResultsCountResponse) => {
            return new SearchBoardActions.GetSearchResultsCountSuccess(results.count);
          }),
          catchError(() => of(new SearchBoardActions.GetSearchResultsCountFailure()))
        );
      })
    ));

  handleSearchResults$: Observable<SearchResultsActions> = createEffect(() => this.actions$
    .pipe(
      ofType(SearchBoardActions.HANDLE_SEARCH_RESULTS),
      withLatestFrom(
        this.store$.pipe(select(selectMaxRecordsToSearch)),
        this.store$.pipe(select(selectIsMobile)),
        this.store$.pipe(
          select(selectPreferenceElement(PreferenceCode.PG_COMMON_PREFERENCE, PreferenceCode.PREF_AUTO_OPEN_SINGLE_PROPERTY))
        )
      ),
      map(([action, maxRecordsToSearch, isMobile, singlePropPref]:
             [SearchBoardActions.HandleSearchResults, number, boolean, PreferenceElement]) => {
        const resultActions: Array<SearchResultsActions> = [];
        const searchResultData = action.payload.searchResultData as SearchResultData;
        const searchParameters = action.payload.searchParameters;
        const searchPayload = action.payload.searchPayload;

        if (searchResultData && searchResultData.propertySummaryList) {
          const listLength = searchResultData.propertySummaryList.length;
          const currentLimit = isMobile ? MOBILE_MAX_RECORDS_TO_SEARCH : maxRecordsToSearch;

          if (searchPayload.exportResults) {
            if (searchPayload.searchRequestType === SearchRequestTypes.EXPORT_RESULT) {
              const propertyIdentifiers = searchResultData.propertySummaryList.map((selectedRecord) => selectedRecord.propertyIdentifier);
              resultActions.push(new SearchBoardActions.RunExport({dataElements: searchPayload.dataElements, propertyIdentifiers}));
            }

            if (searchPayload.searchRequestType === SearchRequestTypes.EXPORT_LABELS) {
              const propertyIdentifiers = searchResultData.propertySummaryList.map((selectedRecord) => selectedRecord.propertyIdentifier);
              resultActions.push(new SearchBoardActions.RunLabelsExport({ ...(action.payload.searchPayload.searchRequestData as Object ?? {}), propertyIdentifiers }));
            }

            return resultActions;
          }

          if (listLength === 1 && PreferenceUtils.hasPreferenceValue(singlePropPref, DisplayOptions.YES)) {
            this.navigationService.openSingleReport(searchResultData.propertySummaryList[0]);
            resultActions.push(new SearchBoardActions.SearchSuccess(searchResultData));
            return resultActions;
          }

          if (listLength >= currentLimit) {
            resultActions.push(new SearchBoardActions.SearchLimitExceeded({
              maxRecordsToSearch: currentLimit,
              searchResultData,
              searchParameters,
              searchPayload
            }));
            return resultActions;
          }
        }

        searchResultData.propSuggestion ?
          resultActions.push(new SearchBoardActions.PropertySuggestion({
            searchResultData,
            searchParameters
          })) :
          resultActions.push(new SearchBoardActions.SearchSuccess(searchResultData));

        return resultActions;
      }),
      switchMap((actions: Array<SearchResultsActions>) => actions)
    ));

  searchSuccess$: Observable<SelectAllProperty | ChangeSidePanelView> = createEffect(() => this.actions$
    .pipe(
      ofType(SearchBoardActions.SEARCH_SUCCESS),
      withLatestFrom(this.store$.pipe(select(selectIsMobile))),
      map(([action, isMobile]: [SearchBoardActions.SearchSuccess, boolean]) => {
        const resultActions: Array<SelectAllProperty | ChangeSidePanelView> = [];
        if (isMobile) {
          resultActions.push(new ChangeSidePanelView(false));

          if (action.payload && (action.payload.propertySummaryList || []).length > 1) {
            resultActions.push(new SelectAllProperty(false));
          }
        }
        return resultActions;
      }),
      filter((actions: Array<SelectAllProperty | ChangeSidePanelView>) => !!actions.length),
      switchMap((actions: Array<SelectAllProperty | ChangeSidePanelView>) => actions)
    ));

  propertySuggestion$: Observable<SearchBoardActions.QuickSearch> = createEffect(() => this.actions$
    .pipe(
      ofType(SearchBoardActions.PROPERTY_SUGGESTION),
      withLatestFrom(
        this.store$.pipe(select(selectIsMobile)),
        this.store$.pipe(select(selectSelectedGeos)),
        this.store$.pipe(select(selectSelectedGridView)),
      ),
      switchMap(([action, isMobile, selectedGeos, selectedGridView]:
                   [SearchBoardActions.PropertySuggestion, boolean, Array<Region>, GridViewState]) => {
          let sidePanelTabOpened: SidePanelTab;

          this.store$
            .pipe(
              select(selectSidePanelState),
              untilDestroyed(this)
            )
            .subscribe((sidePanelView: SidePanelView) => {
              sidePanelTabOpened = sidePanelView.sidePanelTabOpened;
            });

          this.mixpanelService.track('Search - Property Suggestion Displayed', {
            searchType: sidePanelTabOpened,
            isSavedSearch: (sidePanelTabOpened === 'quickSearch') ? false :
              this.searchMixpanelUtil.selectedSearch !== 'My Search Default',
            searchParameters: action.payload.searchParameters,
            countyList: selectedGeos.map((x) => x.fips),
            resultDisplay: this.gridViewState(selectedGridView),
            numberOfResults: action.payload.searchResultData.totalRecords
          });
          return this.dialog.open(PropertySuggestionComponent, {
            panelClass: ModalType.SMALL,
            data: {
              results: action.payload.searchResultData,
              isMobile: isMobile
            }
          }).afterClosed();
        }
      ),
      filter(Boolean),
      map(({propertyIdentifiers}: { propertyIdentifiers: Array<PropertyIdentifier> }) => {
        return new SearchBoardActions.QuickSearch({
          searchFields: [],
          propertyIdentifiers
        });
      })
    ));

  searchLimitExceeded$: Observable<SearchLimitExceededActions> = createEffect(() => this.actions$
    .pipe(
      ofType(SearchBoardActions.SEARCH_LIMIT_EXCEEDED),
      switchMap((action: SearchBoardActions.SearchLimitExceeded) => {
        return this.dialog.open(SearchMaximumResultsComponent, {
          panelClass: ModalType.CONFIRMATION,
          data: {maxRecordsToSearch: action.payload.maxRecordsToSearch}
        })
          .afterClosed()
          .pipe(
            withLatestFrom(
              this.store$.select(selectSidePanelTab),
              this.store$.pipe(select(selectTemplate(TemplateCode.TMPL_MY_SEARCH_DEFAULT))),
              this.store$.pipe(select(selectSelectedGeos))
            ),
            map(([selectedAction, sidePanelTab, searchTemplate, selectedGeos]) => {
              switch (selectedAction) {
                case SearchMaximumResultsAction.SHOW_RESULTS: {
                  const propertySummaryList = action.payload.searchResultData.propertySummaryList
                    .slice(0, action.payload.maxRecordsToSearch);
                  this.mixpanelService.track('Search - Maximum Results Click', {
                    searchType: sidePanelTab,
                    isSavedSearch: searchTemplate?.label !== 'My Search Default',
                    searchParameters: action.payload.searchParameters,
                    countyList: selectedGeos.map((x) => x.fips),
                    continueSelection: selectedAction,
                    maxLimit: action.payload.maxRecordsToSearch
                  });

                  return new SearchBoardActions.SearchSuccess({
                    propertySummaryList,
                    totalRecords: action.payload.maxRecordsToSearch
                  });
                }
                case SearchMaximumResultsAction.CREATE_LABELS: {
                  return new SearchBoardActions.StartLabelsExport({ checkedProperties: action.payload.searchResultData.propertySummaryList, shouldUseCheckedPropertiesCount: false });
                }
                case SearchMaximumResultsAction.EXPORT_RESULTS: {
                  return new SearchBoardActions.StartCustomExport({ checkedProperties: action.payload.searchResultData.propertySummaryList, shouldUseCheckedPropertiesCount: false });
                }
                case SearchMaximumResultsAction.REFINE_SEARCH:
                default: {
                  return null;
                }
              }
            })
          );
      }),
      filter<SearchLimitExceededActions>(Boolean)
    ));

  getLotDimensions$: Observable<SearchBoardActions.GetLotDimensionsSuccess | SearchBoardActions.ShowError> = createEffect(() => this.actions$
    .pipe(
      ofType(SearchBoardActions.GET_LOT_DIMENSIONS),
      this.spinnerOverlayService.wrapRequest((action: SearchBoardActions.GetLotDimensions) => {
        return this.searchService.getLotDimensions(action.payload).pipe(
          catchError(() => of(null))
        );
      }, ActionName.GET_LOT_DIMENSIONS),
      map((results: Array<LotDimension>) => results
        ? new SearchBoardActions.GetLotDimensionsSuccess(results)
        : new SearchBoardActions.ShowError('Lot dimensions are not available for this property'))
    ));

  getPropertyInformation$: Observable<SearchBoardActions.GetPropertyInformationSuccess> = createEffect(() => this.actions$
    .pipe(
      ofType(SearchBoardActions.GET_PROPERTY_INFORMATION),
      withLatestFrom(
        this.store$.pipe(select(selectPreferenceValue(PreferenceCode.PG_RL_PREMIUM_SEARCH, PreferenceCode.PREF_RL_PREMIUM_SEARCH))),
        this.store$.pipe(select(selectSidePanelTab)),
        this.store$.pipe(select(selectedMapLayer))
      ),
      switchMap(([action, isPremium, sidePanelTab, layer]: any) => {
        return this.searchService
          .getPropertyInformation(
            action.payload,
            isPremium === 'Y' && sidePanelTab === SidePanelTab.mySearch && layer[0]
              ? layer[0].fieldCode === 'PREM_SELL_SCORE' ? layer[0].fieldCode : `${layer[0].fieldCode},PREM_SELL_SCORE`
              : null)
          .pipe(
            catchError(() => of(null)
            )
          );
      }),
      map((results: MapPropertyInfo) => new SearchBoardActions.GetPropertyInformationSuccess(results))
    ));

  getMapLegendData: Observable<SearchBoardActions.GetMapLegendDataSuccess | SearchBoardActions.GetMapLegendDataFailure> = createEffect(() => this.actions$.pipe(
    ofType(SearchBoardActions.GET_MAP_LEGEND_DATA),
    switchMap((action: SearchBoardActions.GetMapLegendData) => {
      return this.searchService.getLegendData(action.payload).pipe(
        catchError(() => of([]))
      );
    }),
    map((rangeInfo: Array<RangeInfo>) => new SearchBoardActions.GetMapLegendDataSuccess(rangeInfo)),
    catchError(() => of(new SearchBoardActions.GetMapLegendDataFailure()))
  ));

  getTrendsLayerData: Observable<SearchBoardActions.GetTrendsLayerDataSuccess> = createEffect(() => this.actions$.pipe(
    ofType(SearchBoardActions.GET_TRENDS_LAYER_DATA),
    switchMap((action: SearchBoardActions.GetTrendsLayerData) => {
      return this.searchService.getTrendsLayerData(action.payload).pipe(
        catchError(() => of(null))
      );
    }),
    filter(Boolean),
    map((trendsLayerData: Array<TrendsLayerData>) => new SearchBoardActions.GetTrendsLayerDataSuccess(trendsLayerData))
  ));

  getFloodZoneData: Observable<SearchBoardActions.GetFloodZoneDataSuccess> = createEffect(() => this.actions$.pipe(
    ofType(SearchBoardActions.GET_FLOOD_ZONE_DATA),
    switchMap((action: SearchBoardActions.GetFloodZoneData) => {
      return this.searchService.getFloodZoneData(action.payload).pipe(
        catchError(() => of(null))
      );
    }),
    filter(Boolean),
    map((floodZoneData: Array<FloodZoneData>) => new SearchBoardActions.GetFloodZoneDataSuccess(floodZoneData))
  ));

  showRibbonError: Observable<SearchBoardActions.ShowError> = createEffect(() => this.actions$.pipe(
    ofType(SearchBoardActions.SHOW_ERROR),
    tap((action: SearchBoardActions.ShowError) => this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, action.payload))
  ), {dispatch: false});

  getLookUps: Observable<SearchBoardActions.GetLookUpsSuccess> = createEffect(() => this.actions$.pipe(
    ofType(SearchBoardActions.GET_LOOK_UPS),
    withLatestFrom(this.store$.pipe(select(selectSelectedGeos))),
    mergeMap(([action, counties]: [SearchBoardActions.GetLookUps, Array<Region>]) => {
      return this.searchService.getLookUps(this.filterOnlyLookupsAttributes(action.payload), counties).pipe(
        catchError(() => of({}))
      );
    }),
    map((lookUpData: { [key: string]: Array<LookUpData> }) =>
      new SearchBoardActions.GetLookUpsSuccess(lookUpData)
    )
  ));

  getLookUpsForNewRegions: Observable<SearchBoardActions.GetLookUpsSuccess> = createEffect(() => this.actions$.pipe(
    ofType(SearchBoardActions.GET_LOOK_UPS_FOR_NEW_REGIONS),
    withLatestFrom(this.store$.pipe(select(selectSelectedSearch))),
    switchMap(([action, template]: [SearchBoardActions.GetLookUpsForNewRegions, Template]) => {
      return this.searchService.getLookUps(this.filterOnlyLookupsAttributes(template.dataElementList), action.payload).pipe(
        catchError(() => of(null))
      );
    }),
    map((lookUpData: { [key: string]: Array<LookUpData> }) =>
      new SearchBoardActions.GetLookUpsSuccess(lookUpData)
    )
  ));

  getLookupsForSearchMultiField: Observable<SearchBoardActions.GetLookupsForSearchMultiFieldSuccess> = createEffect(() => this.actions$.pipe(
    ofType(SearchBoardActions.GET_LOOK_UPS_FOR_SEARCH_MULTI_FIELD),
    this.spinnerOverlayService.wrapRequest((action: SearchBoardActions.GetLookupsForSearchMultiField) =>
        this.searchService.getLookupsForSearchMultiField(action.payload).pipe(
          catchError(() => of(null))
        )
      , ActionName.GET_LOOK_UPS_FOR_SEARCH_MULTI_FIELD),
    map((searchedAttributes: Array<string>) => {
      const lookUps = searchedAttributes.map((item: string) => {
        return {code: item, label: item};
      });
      return new SearchBoardActions.GetLookupsForSearchMultiFieldSuccess(lookUps);
    })
  ));

  private gridViewState(gridViewState: GridViewState): string {
    if (gridViewState.list > 0 && gridViewState.map > 0) {
      return 'spit';
    } else if (gridViewState.list === 100) {
      return 'list';
    } else if (gridViewState.map === 100) {
      return 'map';
    }
    return '';
  }

  private hasSearchCountFields(searchFields: Array<SearchField>): boolean {
    return searchFields.some((field: SearchField) => {
      const operatorValue = field.operatorValues[0];
      if (operatorValue.values) {
        if (operatorValue.operator === SearchOperator.STARTS_WITH) {
          return this.hasSearchCountValue(operatorValue.values[0]) || this.hasSearchCountValue(operatorValue.values[1]);
        }
        return true;
      }
      return false;
    });
  }

  private hasSearchCountValue(fieldValue: number | string): boolean {
    return fieldValue && ('' + fieldValue).length >= SEARCH_RESULTS_COUNT_MIN_LENGTH;
  }

  private filterOnlyLookupsAttributes(elements: Array<DataElement>): Array<DataElement> {
    const renderIdList =
      [RENDER_ID.DIALOGBOX_LOOKUP,
        RENDER_ID.SINGLE_SELECT,
        RENDER_ID.DROPDOWN_MENU,
        RENDER_ID.DIALOGBOX_LOOKUP_WITH_DATA_LOAD,
        RENDER_ID.MULTI_SELECT_SEARCH_LOOKUP];
    // LENDER_CODE (renderId 9) is intentionally excluded from the bulk get-lookup call to avoid the
    // vendor lender-name search timeout; it is resolved via /api/preference/get-lookup-by-token instead.
    // This mirrors the backend PreferenceController, which resolves renderId 9 but skips
    // FieldCodeConstants.LENDER_CODE. Keeping renderId 9 here (minus LENDER_CODE) lets premium-map
    // fields such as PREM_SELL_SCORE resolve their lookups again.
    return elements.filter((dataElement: DataElement) =>
      renderIdList.indexOf(dataElement.displayInfo.renderId) !== -1 &&
      dataElement.fieldCode !== 'LENDER_CODE');
  }
}
